import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { FileInput, PromptTypes } from 'generated/graphql-types';
import { Observable, Subject } from 'rxjs';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { PromptDetailFormService } from '../../prompt-details/services/prompt-detail-form.service';
import { AddPromptData } from '../models/add-prompt-data';
import { FileDetailModel, FileSaveModel } from '../models/save-file-only-models';
import {
   createNewFromExtensionPrompt,
   createPrompt,
   createPromptAndAddAnother,
   recordPrompt,
   recordPromptCancelPressed,
   recordPromptFileOnly,
   saveFileOnly,
   setRecordingFromExtensionVariables,
} from '../state/add-prompt.actions';
import { getAddPromptCallState, getRecordingPromptCallStatus } from '../state/add-prompt.selectors';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   providers: [PromptDetailFormService],
   selector: 'ndt-add-prompt',
   styleUrls: ['./add-prompt.component.scss'],
   templateUrl: './add-prompt.component.html',
   standalone: false,
})
export class AddPromptComponent implements OnInit, OnDestroy, FormComponent {
   excludedPromptTypes: PromptTypes[];

   includedPromptTypes: PromptTypes[] | null;

   callState: Observable<CallState>;

   recordingPromptCallStatus$: Observable<string | null>;

   form: UntypedFormGroup;

   creatingNewFromExtension = false;

   includeReusableOption = false;

   inlcudeAddAnother = false;

   onlyPromptTypeOption: PromptTypes;

   routeToNewPromptAfterSave: boolean | null;

   promptIdIfUpdatingExistingPromptFile: number | null;

   promptName: string | null;

   isPromptForAccountMoh?: boolean | null;

   isPromptForExtensionQueueMoh?: boolean | null;

   private destroyed$ = new Subject<void>();

   constructor(
      protected store: Store,
      protected formService: PromptDetailFormService,
      protected router: Router,
      private fb: UntypedFormBuilder,
      @Inject(MAT_DIALOG_DATA) public data: AddPromptData,
      private dialogService: LcsDialogService,
      public dialogRef: MatDialogRef<AddPromptComponent>
   ) {
      if (this.data !== null) {
         if (this.data.creatingNewFromExtension !== null) {
            this.creatingNewFromExtension = data.creatingNewFromExtension;
         }
         if (this.data.includeReusableOption !== null) {
            this.includeReusableOption = data.includeReusableOption;
         }
         if (this.data.includedPromptTypes !== null) {
            this.includedPromptTypes = data.includedPromptTypes;
         } else {
            this.includedPromptTypes = null;
         }
         if (this.data.excludedPromptTypes !== null) {
            this.excludedPromptTypes = data.excludedPromptTypes;
         }
         if (this.data.routeToNewPromptAfterSave !== null) {
            this.routeToNewPromptAfterSave = data.routeToNewPromptAfterSave;
         }
         if (this.data.isPromptForAccountMoh !== null) {
            this.isPromptForAccountMoh = data.isPromptForAccountMoh;
         }
         if (this.data.onlyPromptTypeOption !== null) {
            this.onlyPromptTypeOption = data.onlyPromptTypeOption;
         }
         if (this.data.promptId !== null && this.data.promptId !== undefined) {
            this.promptIdIfUpdatingExistingPromptFile = data.promptId;
         } else {
            this.promptIdIfUpdatingExistingPromptFile = null;
         }
         if (this.data.name !== null) {
            this.promptName = data.name;
         }
         this.form = this.fb.group({
            prompt: this.fb.control(null),
         });

         this.inlcudeAddAnother = data.includeAddAnother;

         this.callState = this.store.pipe(select(getAddPromptCallState));
         this.recordingPromptCallStatus$ = this.store.pipe(select(getRecordingPromptCallStatus));
      }
   }

   ngOnInit(): void {
      this.form = this.formService.form;
      let availablePromptTypes = Object.values(PromptTypes);

      if (this.excludedPromptTypes && this.excludedPromptTypes.length > 0) {
         availablePromptTypes = availablePromptTypes.filter((type) => {
            return !this.excludedPromptTypes.includes(type);
         });
      }

      if (this.includedPromptTypes && this.includedPromptTypes.length > 0) {
         availablePromptTypes = availablePromptTypes.filter((type) => {
            return this.includedPromptTypes?.includes(type);
         });
      }

      if (availablePromptTypes.length === 1 && this.onlyPromptTypeOption === null) {
         this.onlyPromptTypeOption = availablePromptTypes[0];
      }
   }

   ngOnDestroy(): void {
      this.store.dispatch(recordPromptCancelPressed());
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   onCreate(isContinuous: boolean): void {
      if (this.promptIdIfUpdatingExistingPromptFile !== null) {
         {
            //save file only

            let file: File | null;

            file = this.formService.getFile();

            let fileDetail: FileDetailModel | null = null;

            let fileFields = ['accountId', 'fileName'] as (keyof FileInput)[];

            let fileModelToSave: FileSaveModel | null = null;

            if (file) {
               fileDetail = {
                  accountId: this.data.accountId,
                  fileName: file.name,
                  fileId: null,
               };
            }

            if (file && fileDetail && fileFields) {
               fileModelToSave = {
                  file: file,
                  fileDetail: fileDetail,
                  fields: fileFields,
               };

               this.store.dispatch(
                  saveFileOnly({
                     fileModelToSave,
                  })
               );
            }
         }
      }

      if (this.form.valid && this.promptIdIfUpdatingExistingPromptFile === null) {
         const file = this.formService.getFile();
         const prompt = this.formService.getValue(null);
         if (this.creatingNewFromExtension) {
            prompt.typeId = this.data.promptTypeId;
         } else {
            if (!this.includeReusableOption) {
               prompt.isReusable = true;
            }
         }

         if (this.onlyPromptTypeOption) {
            prompt.typeId = this.onlyPromptTypeOption;
         }

         if (file) {
            prompt.file = {
               accountId: this.data.account?.accountId ? Number(this.data.account.accountId) : null,
               fields: ['accountId', 'metaTag', 'promptType', 'isPromptForAccountMoh', 'isPromptForExtensionQueueMoh'],
               isPromptForAccountMoh: this.isPromptForAccountMoh === true ? true : null,
               isPromptForExtensionQueueMoh: this.isPromptForExtensionQueueMoh === true ? true : null,
               metaTag: file.name,
               promptType: prompt.typeId,
            };
         }

         if (this.creatingNewFromExtension) {
            this.store.dispatch(
               createNewFromExtensionPrompt({
                  file,
                  prompt,
                  promptFields: [],
                  routeToNewPromptAfterSave: this.routeToNewPromptAfterSave,
                  voiceMailGreetingPrompt: this.data.voiceMailGreetingPrompt,
               })
            );
         } else {
            if (!isContinuous) {
               this.store.dispatch(
                  createPrompt({
                     file,
                     prompt,
                     promptFields: [],
                     routeToNewPromptAfterSave: this.routeToNewPromptAfterSave,
                     voiceMailGreetingPrompt: null,
                  })
               );
            } else {
               this.store.dispatch(
                  createPromptAndAddAnother({
                     file,
                     prompt,
                     promptFields: [],
                     routeToNewPromptAfterSave: this.routeToNewPromptAfterSave,
                     voiceMailGreetingPrompt: null,
                  })
               );
            }
         }
      }
      if (!isContinuous) {
         this.dialogRef.close(this.form.value);
      }
   }

   onRecord(isContinuous: boolean): void {
      if (this.form.valid) {
         const prompt = this.formService.getValue(null);
         const destinationExtension = this.formService.getDestinationExtension();
         const destinationNumber = this.formService.getDestinationNumber();
         const file = this.formService.getFile();
         if (this.creatingNewFromExtension) {
            prompt.typeId = this.data.promptTypeId;
            this.store.dispatch(
               setRecordingFromExtensionVariables({
                  creatingNewFromExtension: this.creatingNewFromExtension,
                  recordingNewFromAdd: false,
                  voiceMailGreetingPrompt: this.data.voiceMailGreetingPrompt,
               })
            );
         } else {
            prompt.isReusable = true;
            this.store.dispatch(
               setRecordingFromExtensionVariables({
                  creatingNewFromExtension: this.creatingNewFromExtension,
                  recordingNewFromAdd: true,
                  voiceMailGreetingPrompt: null,
               })
            );
         }

         if (
            this.promptIdIfUpdatingExistingPromptFile !== null &&
            this.promptIdIfUpdatingExistingPromptFile !== undefined
         ) {
            this.store.dispatch(
               recordPromptFileOnly({
                  promptId: this.promptIdIfUpdatingExistingPromptFile,
                  name: this.promptName ?? '',
                  promptType: this.onlyPromptTypeOption,
                  destinationNumber: destinationNumber,
                  routeToExtension: destinationExtension,
               })
            );
         } else {
            this.store.dispatch(
               recordPrompt({
                  destinationNumber: destinationNumber,
                  prompt: {
                     file,
                     prompt,
                     promptFields: [],
                     routeToNewPromptAfterSave: this.routeToNewPromptAfterSave,
                     voiceMailGreetingPrompt: null,
                  },
                  routeToExtension: destinationExtension,
                  isContinuous: isContinuous,
               })
            );
         }
      }
   }

   promptDisplayWith(option: PromptTypes): string {
      return `${option ?? ''}`;
   }

   getPromptTypes(): PromptTypes[] {
      let promptTypes = Object.values(PromptTypes);

      if (this.excludedPromptTypes && this.excludedPromptTypes.length > 0) {
         promptTypes = promptTypes.filter((type) => {
            return !this.excludedPromptTypes.includes(type);
         });
      }

      if (this.includedPromptTypes && this.includedPromptTypes.length > 0) {
         promptTypes = promptTypes.filter((type) => {
            return this.includedPromptTypes?.includes(type);
         });
      }
      return promptTypes;
   }

   onCancelPromptRecordingCall() {
      this.store.dispatch(recordPromptCancelPressed());
   }
}
